import { useEffect, useState } from 'preact/hooks';
import { createBackup, passwordLongEnough } from '../core';
import { formatNumber } from '../app/format';
import { APP_VERSION, CONTACT_EMAIL, type Language, Prefs } from '../app/prefs';
import { type PendingFaceID, Vault } from '../app/vault';
import { Group, PinPad, PlainButton, PrimaryButton, Row, Sheet, Toggle, copyText } from './components';
import { startBackupFromSettings } from './home';
import { Bell, Check, Copy, Doc, Download, Envelope, EyeOff, FaceID, Globe, Key, People, Shield, Terminal, Trash, Warning } from './icons';
import { isStandalone } from './install';
import { ui, useApp } from './state';

export function SettingsView() {
  const { s, model } = useApp();
  const [phrase, setPhrase] = useState<string[] | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [faceSupported, setFaceSupported] = useState(false);
  const [enablingFace, setEnablingFace] = useState<PendingFaceID | null>(null);
  const [faceError, setFaceError] = useState<string | null>(null);
  const [notifError, setNotifError] = useState<string | null>(null);
  const lang = Prefs.currentLanguage();

  useEffect(() => {
    void Vault.faceIDSupported().then(setFaceSupported);
    return () => setPhrase(null);
  }, []);

  if (enablingFace) {
    return (
      <Sheet class="auth-sheet" right={<PlainButton onClick={() => setEnablingFace(null)}>{s.cancel}</PlainButton>}>
        <PinPad title={s.confirmWithPin} subtitle={s.faceIDRow} error={faceError}
          onComplete={async (pin) => {
            setFaceError(null);
            try {
              if (await model.finishFaceID(pin, enablingFace)) setEnablingFace(null);
              else setFaceError(s.faceIDFailed);
            } catch {
              setFaceError(s.faceIDFailed);
            }
          }} />
      </Sheet>
    );
  }

  return (
    <div class="tab-page">
      <h1 class="large-title page-title">{s.settings}</h1>

      <Group>
        <Row icon={<Globe size={20} />} label={s.language}>
          <select class="select" value={lang} aria-label={s.language}
            onChange={(e) => ui.setLanguage((e.target as HTMLSelectElement).value as Language)}>
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </Row>
      </Group>

      <Group>
        <Row icon={<Warning size={20} />} label={s.webWarningsRow} chevron onClick={() => ui.open({ type: 'webWarnings' })} />
      </Group>

      <Group title={s.securityBackup} footer={s.iCloudFooter}>
        <Row icon={<Shield size={20} />} label={s.backupSettingsRow} onClick={() => void startBackupFromSettings()}
          value={<span class={Prefs.backupDone() ? 'dim' : 'error'}>{Prefs.backupDone() ? s.backupStatusDone : s.backupStatusTodo}</span>} />
        {phrase ? (
          <div class="pad stack-10">
            <div class="word-grid compact secret">
              {phrase.map((w, i) => <div class="word"><span class="num">{i + 1}</span><span class="mono">{w}</span></div>)}
            </div>
            <p class="error small">{s.phraseWarning}</p>
            <PlainButton class="strong" onClick={() => setPhrase(null)}>{s.hidePhrase}</PlainButton>
          </div>
        ) : (
          <Row icon={<Key size={20} />} label={s.showPhrase} onClick={async () => {
            const p = await model.revealPhrase();
            if (p) setPhrase(p.split(' '));
          }} />
        )}
        <Row icon={<FaceID size={20} />} label={s.faceIDRow}>
          {faceSupported
            ? <Toggle label={s.faceIDRow} checked={Vault.hasFaceID()} onChange={(v) => {
                if (!v) return model.disableFaceID();
                setFaceError(null);
                // Create the passkey right away (Face ID prompt), then confirm with the passcode.
                void Vault.startFaceID().then((p) => (p ? setEnablingFace(p) : setFaceError(s.faceIDFailed))).catch(() => setFaceError(s.faceIDFailed));
              }} />
            : <span class="row-value dim small">—</span>}
        </Row>
        <Row icon={<Download size={20} />} label={s.fileBackupRow} chevron onClick={() => ui.open({ type: 'fileBackup' })} />
        <Row icon={<Terminal size={20} />} label={s.exportCoreRow} chevron onClick={() => ui.open({ type: 'exportCore' })} />
      </Group>
      {faceError && !enablingFace && <p class="group-footer outside error">{faceError}</p>}
      {!faceSupported && <p class="group-footer outside">{s.faceIDUnsupported}</p>}
      {faceSupported && <p class="group-footer outside">{s.faceIDFooter}</p>}

      <Group>
        <Row icon={<People size={20} />} label={s.contactsTitle} chevron onClick={() => ui.open({ type: 'contacts' })} />
        <Row icon={<EyeOff size={20} />} label={s.hideBalance}>
          <Toggle label={s.hideBalance} checked={Prefs.hideBalance()} onChange={(v) => { Prefs.setHideBalance(v); ui.dispatchEvent(new Event('change')); }} />
        </Row>
      </Group>

      <Group title={s.notifications} footer={notifError ?? s.notificationsFooter}>
        <Row icon={<Bell size={20} />} label={s.notificationsRow}>
          <Toggle label={s.notificationsRow} checked={Prefs.notifications() && notifPermission() === 'granted'} onChange={async (v) => {
            setNotifError(null);
            if (!v) { Prefs.setNotifications(false); ui.dispatchEvent(new Event('change')); return; }
            if (!('Notification' in window)) { setNotifError(s.notificationsFooter); return; }
            const p = await Notification.requestPermission();
            if (p === 'granted') Prefs.setNotifications(true);
            else setNotifError(s.notificationsDenied);
            ui.dispatchEvent(new Event('change'));
          }} />
        </Row>
      </Group>

      <Group title={s.network}>
        <Row label={s.server} value={model.serverHost || '—'} />
        {model.snapshot && model.snapshot.height > 0 && <Row label={s.block} value={formatNumber(model.snapshot.height)} />}
      </Group>

      <Group title={s.about}>
        <Row icon={<Doc size={20} />} label={s.legalDocumentTitle} chevron onClick={() => ui.open({ type: 'legal' })} />
        {!isStandalone() && <Row icon={<Download size={20} />} label={s.installRow} chevron onClick={() => ui.open({ type: 'install' })} />}
        <Row label={s.version} value={`${APP_VERSION} · ${s.webFooter}`} />
        <a class="row row-button" href="https://coinwow.ca" target="_blank" rel="noopener noreferrer"><span class="row-icon"><Globe size={20} /></span><span class="row-label">coinwow.ca</span></a>
        <Row icon={emailCopied ? <Check size={20} /> : <Envelope size={20} />} label={CONTACT_EMAIL} value={emailCopied ? s.copied : undefined}
          onClick={() => { copyText(CONTACT_EMAIL).then(() => setEmailCopied(true)).catch(() => {}); }} />
      </Group>

      <Group footer={s.deleteWalletFooter}>
        {confirmDelete ? (
          <>
            <div class="pad small">{s.deleteWalletConfirm}</div>
            <Row icon={<Trash size={20} />} danger label={s.delete} onClick={async () => {
              setConfirmDelete(false);
              await model.forgetWallet();
            }} />
            <Row label={s.cancel} onClick={() => setConfirmDelete(false)} />
          </>
        ) : <Row danger label={s.deleteWallet} onClick={() => setConfirmDelete(true)} />}
      </Group>
    </div>
  );
}

function notifPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Commands to restore the wallet in COINWOW Core, without our servers. */
export function ExportCoreSheet() {
  const { s, model } = useApp();
  const [cmds, setCmds] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(-1);
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    if (asked) return;
    setAsked(true);
    void model.exportForCore().then((c) => (c ? setCmds(c) : ui.close()));
  }, []);

  return (
    <Sheet title={s.exportCoreTitle} right={<PlainButton onClick={() => ui.close()}>{s.done}</PlainButton>}>
      <p>{s.exportCoreText}</p>
      <p class="error small">{s.exportCoreWarning}</p>
      {cmds?.map((c, i) => (
        <div class="card pad stack-10 secret">
          <div class="section-title">{i + 1}</div>
          <code class="mono small break selectable">{c}</code>
          <PlainButton class="strong small" onClick={async () => { await copyText(c).catch(() => {}); setCopied(i); }}>
            {copied === i ? <Check size={16} /> : <Copy size={16} />} {s.copyCommand}
          </PlainButton>
        </div>
      ))}
    </Sheet>
  );
}

/** Creates an encrypted backup file (format v1): the phrase never leaves the device unencrypted. */
export function FileBackupSheet() {
  const { s, model } = useApp();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && !passwordLongEnough(password);
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = passwordLongEnough(password) && confirm === password && !busy;

  const create = async () => {
    setError(null);
    setBusy(true);
    try {
      const phrase = await model.revealPhrase(); // asks for the passcode first
      if (!phrase) return;
      const file = await createBackup(phrase, password);
      const blob = new Blob([file], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `COINWOW-backup-${new Date().toISOString().slice(0, 10)}.coinwowbackup`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setPassword('');
      setConfirm('');
      setDone(true);
    } catch {
      setError(s.genericError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={s.fileBackupTitle} right={<PlainButton onClick={() => ui.close()}>{s.done}</PlainButton>}>
      <p>{s.fileBackupIntro}</p>
      <p class="error small">{s.fileBackupWarning}</p>
      <Group title={s.fileBackupPassword}>
        <input class="row-input" type="password" autocomplete="new-password" autocapitalize="off" spellcheck={false}
          value={password} onInput={(e) => { setPassword((e.target as HTMLInputElement).value); setDone(false); }} />
      </Group>
      <Group title={s.fileBackupConfirm}>
        <input class="row-input" type="password" autocomplete="new-password" autocapitalize="off" spellcheck={false}
          value={confirm} onInput={(e) => { setConfirm((e.target as HTMLInputElement).value); setDone(false); }} />
      </Group>
      {tooShort && <p class="error small">{s.fileBackupTooShort}</p>}
      {mismatch && <p class="error small">{s.fileBackupMismatch}</p>}
      {error && <p class="error">{error}</p>}
      {done && <p class="dim small">{s.fileBackupDone}</p>}
      <PrimaryButton disabled={!ready} onClick={() => void create()}>{busy ? s.fileBackupWorking : s.fileBackupCreate}</PrimaryButton>
    </Sheet>
  );
}
